import React from 'react';
import { Spin } from 'antd';
import { Container } from '@/components/ui/Container';
import { useRoleDetail, groupPermissionsByModule } from '@hooks/rbac/useRoleDetail';
import { formatCreatedRelative, formatLastUpdated } from '@utils/dateFormating.util';
import {
    DetailLabel,
    DetailValue,
    DetailBadge,
    DetailPanelLoading,
    DetailValueInherit,
    DetailSecondaryText,
    ModalSectionHeading,
    DetailPermListWrapped,
    DetailPermChip,
} from './style';

/** "hr" -> "HR", "open_market" -> "Open Market" */
const formatModuleLabel = (s) =>
    s && typeof s === 'string' ? s.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) : s;

/**
 * Content for the role side panel. Use inside <SidePanel>.
 * Only the content changes; the panel shell is reusable.
 */
const RoleDetailPanel = ({ roleId }) => {
    const { data: role, isLoading, isError } = useRoleDetail(roleId);

    if (!roleId) return null;
    if (isLoading) {
        return (
            <DetailPanelLoading>
                <Spin size="large" />
            </DetailPanelLoading>
        );
    }
    if (isError || !role) {
        return (
            <Container $marginBottom="24px">
                <DetailValueInherit>Failed to load role details.</DetailValueInherit>
            </Container>
        );
    }

    const typeLabel = role.isSystemRole ? 'System' : 'Custom';
    const permissions = role.permissions || [];
    const groupedByModule = groupPermissionsByModule(permissions);

    return (
        <>
            <Container $marginBottom="24px" $display="flex" $alignItems="flex-start" $gap="12px">
                <DetailLabel>Role type :</DetailLabel>
                <DetailValue>
                    <DetailBadge>{typeLabel}</DetailBadge>
                </DetailValue>
            </Container>

            <Container $marginBottom="24px" $display="flex" $alignItems="flex-start" $gap="12px">
                <DetailLabel>Created :</DetailLabel>
                <DetailValue>{formatCreatedRelative(role.createdAt)}</DetailValue>
            </Container>

            <Container $marginBottom="24px" $display="flex" $alignItems="flex-start" $gap="12px">
                <DetailLabel>Last updated :</DetailLabel>
                <DetailValue>{formatLastUpdated(role.updatedAt)}</DetailValue>
            </Container>

            <Container $marginBottom="24px" $display="flex" $flexDirection="column" $alignItems="stretch" $gap="12px">
                <DetailLabel>Permissions :</DetailLabel>
                {groupedByModule.length === 0 ? (
                    <DetailSecondaryText>No permissions assigned</DetailSecondaryText>
                ) : (
                    groupedByModule.map(({ module: moduleKey, permissions: perms }) => (
                        <Container key={moduleKey} $marginBottom="4px">
                            <ModalSectionHeading>{formatModuleLabel(moduleKey)}</ModalSectionHeading>
                            <DetailPermListWrapped>
                                {perms.map((p) => (
                                    <DetailPermChip key={p.id}>
                                        {p.key || `${p.module}.${p.feature}.${p.action}`}
                                    </DetailPermChip>
                                ))}
                            </DetailPermListWrapped>
                        </Container>
                    ))
                )}
            </Container>
        </>
    );
};

export default RoleDetailPanel;
